/**
 * Iter 245 — Commandes clients (Shopify + manuelles).
 */
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { ShoppingCart, Loader2, ExternalLink } from "lucide-react";

export default function CustomerOrdersPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inventory/customer-orders");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = filter === "all" ? items : items.filter(i => i.source === filter);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>;

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="customer-orders-page">
      <PageHeader
        icon={<ShoppingCart />}
        title="Commandes clients"
        description={`${items.length} commande(s) reçue(s) (Shopify + saisies manuelles).`}
      />

      <div className="flex gap-1.5">
        {[["all", "Toutes"], ["shopify", "Shopify"], ["manual", "Manuelles"]].map(([k, lbl]) => {
          const count = k === "all" ? items.length : items.filter(i => i.source === k).length;
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === k ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] border border-white/5 text-slate-400 hover:text-white"}`}>
              {lbl} <span className="opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-12 text-center text-slate-500">
          Aucune commande client. Les commandes Shopify arriveront automatiquement via le webhook.
        </div>
      ) : (
        <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="bg-[#0b0811] text-[10px] uppercase text-slate-400">
              <tr>
                <th className="text-left p-3">Order</th>
                <th className="text-left p-3">Source</th>
                <th className="text-left p-3">Client</th>
                <th className="text-right p-3">Lignes</th>
                <th className="text-right p-3">Total HT</th>
                <th className="text-left p-3">Reçue le</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.order_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`order-${o.order_id}`}>
                  <td className="p-3 font-mono text-[11px] text-slate-300">{o.order_id}</td>
                  <td className="p-3">
                    {o.source === "shopify" ? (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-500/20 text-emerald-400">Shopify {o.shopify_order_id ? `#${o.shopify_order_id}` : ""}</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-500/20 text-slate-400">Manuelle</span>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="text-white">{o.customer_name}</div>
                    {o.customer_email && <div className="text-[10px] text-slate-500">{o.customer_email}</div>}
                  </td>
                  <td className="p-3 text-right text-slate-300">{o.lines?.length || 0}</td>
                  <td className="p-3 text-right font-bold text-white">{o.total_ht_eur?.toFixed(2)} €</td>
                  <td className="p-3 text-xs text-slate-500">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
